import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

const API = 'api/Passkey';

/** Default “Remind me later” deferral when calling `passkeyRemindLater`. */
export const PASSKEY_REMIND_AFTER_DAYS = 7;

const post = (path, body) =>
    fetch(`${API}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Browser-Url': window.location.href },
        body: JSON.stringify(body)
    });

// ─── Registration (user must be logged in) ───────────────────────────────────

/**
 * Guides the user through the full passkey registration ceremony.
 * @param {number} userId - From localStorage user.id
 * @param {string} username - From localStorage user.email or user.username
 * @param {string} deviceName - Friendly label entered by the user
 * @returns {Promise<{statusCode: string, message: string}>}
 */
export async function registerPasskey(userId, username, deviceName) {
    const uid = parseInt(userId, 10);

    // Step 1: get creation options from server
    const beginRes = await post('RegisterBegin', { userId: uid, username, deviceName });
    if (!beginRes.ok) throw new Error('Failed to start registration.');
    const { challengeId, options } = await beginRes.json();

    // Step 2: browser prompts the user (fingerprint / face / PIN / security key)
    let attestation;
    try {
        attestation = await startRegistration({ optionsJSON: options });
    } catch (err) {
        // InvalidStateError = the authenticator already has an excluded credential (same device, different browser)
        if (err.name === 'InvalidStateError') {
            throw new Error(
                'A passkey for your account is already saved on this device (likely from another browser). ' +
                'You can use that existing passkey to sign in — no need to register again.'
            );
        }
        // NotAllowedError = user cancelled the prompt, tapped "Cancel", or Safari lost the user-gesture context
        if (err.name === 'NotAllowedError') {
            throw new Error(
                'Registration was cancelled. ' +
                'If a passkey is already registered on this device, use "Sign in with Passkey" instead of registering a new one.'
            );
        }
        throw err;
    }

    // Step 3: send attestation + metadata back to server
    const completeRes = await post('RegisterComplete', {
        userId: uid,
        challengeId,
        deviceName,
        ...attestation,
    });

    if (!completeRes.ok) throw new Error('Registration verification failed.');
    return completeRes.json(); // { statusCode, message }
}

// ─── Authentication (no login required) ──────────────────────────────────────

/**
 * Authenticates the user with a passkey.
 * On success, writes the user to localStorage using the same shape as user.service.js
 * and returns the raw user array (same as Authenticate_D).
 */
export async function loginWithPasskey() {
    // Step 1: get assertion options
    const beginRes = await post('LoginBegin', {});
    if (!beginRes.ok) throw new Error('Passkey sign-in failed.');
    let beginData;
    try { beginData = await beginRes.json(); } catch { throw new Error('Passkey sign-in failed.'); }
    const { challengeId, options } = beginData;

    // Step 2: browser prompts user for their passkey
    let assertion;
    try {
        assertion = await startAuthentication({ optionsJSON: options });
    } catch (err) {
        if (err.name === 'NotAllowedError') {
            throw new Error('Sign-in was cancelled or timed out. Please try again.');
        }
        const msg = (err?.message || '').toLowerCase();
        if (err.name === 'UnknownError' || msg.includes('credential manager')) {
            throw new Error(
                'No passkey is available on this device for this site. ' +
                'Sign in with your password, register a passkey while logged in, then try again.'
            );
        }
        throw err;
    }

    // Step 3: verify assertion on server — returns same shape as Authenticate_D
    const completeRes = await post('LoginComplete', { challengeId, ...assertion });
    if (!completeRes.ok) throw new Error('Passkey sign-in failed.');
    let user;
    try { user = await completeRes.json(); } catch { throw new Error('Passkey sign-in failed.'); }

    // Surface server-side auth errors.
    // statusCode "-1" = general auth failure
    // statusCode "-2" = passkey was removed by admin; user must log in with username/password
    if (user && (user.statusCode === '-1' || user.statusCode === '-2')) {
        throw new Error(user.message || 'Passkey sign-in failed.');
    }

    if (!user || !user.length || !user[0].userId) {
        throw new Error('Passkey authentication failed. No matching account found.');
    }

    // Step 4: store session identically to user.service.js login()
    // Property names mirror exactly what user.service.js accesses from the API response.
    const u = user[0];
    const userObj = {
        userrole:           u.role,
        id:                 u.userId,
        email:              u.userEmail,
        username:           u.userName,
        wholesaler:         u.wholesaler,
        wholesaler_Id:      u.wholesaler_Id,
        distributor_UserId: u.distributor_UserId,
        authdata:           window.btoa((u.userEmail || u.userName || '') + ':' + (u.logg_Set || '')),
        paramsname:         u.paramsName,
        dList:              u.dList,
        isfu:               u.isFu,
        isForAnalytic:      u.isForAnalytic,
        iscompopup:         u.isComPopup,
        overrideCount:      u.overrideCount,
        r_rgh_i_e_b:        window.btoa(String(u.isEnableBroker ?? false)),
        isBrokerTypeUser:   u.isBrokerTypeUser,
        isLegacyUser:       u.isLegacyUser,
        unique_ObjectId:    u.unique_ObjectId,
        isPrescribeData:    u.isPrescribeData,
        showPasskeyOnboarding: u.showPasskeyOnboarding === true,
        fullName:
            u.fullName ||
            u.userFullName ||
            u.displayName ||
            u.FullName ||
            '',
    };

    localStorage.setItem('x_resu_c_dat_presc', u.isPrescribeData);
    localStorage.setItem('user', JSON.stringify(userObj));
    localStorage.setItem('x_resu_t_p', u.isFu);
    localStorage.setItem('x_resu_c_p_p', u.isComPopup);
    localStorage.setItem('_login_time', Date.now().toString());

    return user;
}

// ─── Credential management (user must be logged in) ──────────────────────────

/** Returns the list of passkeys registered for a user. */
export async function getUserPasskeys(userId) {
    const res = await post('GetCredentials', { userId: parseInt(userId, 10) });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
}

/** Removes a passkey credential. */
export async function deletePasskey(userId, credentialId) {
    const res = await post('DeleteCredential', { userId: parseInt(userId, 10), credentialId });
    if (!res.ok) throw new Error('Failed to remove passkey.');
    return res.json();
}

/**
 * Whether the server wants to show the post-login passkey onboarding popup.
 * @returns {Promise<{ showPopup?: boolean, ShowPopup?: boolean, reason?: string, skipCount?: number, lastSkippedAt?: string | null, remindAfterDate?: string | null }>}
 */
export async function getPasskeyPopupStatus(userId) {
    const res = await post('popup-status', { UserId: parseInt(userId, 10) });
    if (!res.ok) throw new Error('Failed to load passkey popup status.');
    return res.json();
}

/** Normalizes camelCase / PascalCase from API. */
export function passkeyStatusShouldShowPopup(status) {
    if (!status || typeof status !== 'object') return false;
    return status.showPopup === true || status.ShowPopup === true;
}

/**
 * @returns {Promise<{ statusCode?: string | number, message?: string, StatusCode?: string | number, Message?: string }>}
 */
export async function passkeyRemindLater(userId, remindAfterDays = PASSKEY_REMIND_AFTER_DAYS) {
    const res = await post('remind-later', {
        UserId: parseInt(userId, 10),
        RemindAfterDays: remindAfterDays,
    });
    if (!res.ok) throw new Error('Could not save reminder preference.');
    return res.json();
}

/** True for current API (`statusCode` 200) or legacy (`StatusCode` 1). */
export function isPasskeyRemindLaterSuccess(res) {
    if (!res || typeof res !== 'object') return false;
    const code = res.statusCode ?? res.StatusCode;
    const s = String(code);
    return s === '200' || code === 200 || s === '1' || code === 1;
}
